import { FunctionComponent, ReactElement, SVGProps } from 'react';
import { Icon } from '@cbhq/cds-web/icons';
import { HStack } from '@cbhq/cds-web/layout';
import { Tooltip } from '@cbhq/cds-web/overlays/Tooltip/Tooltip';
import { Link, TextBody, TextHeadline } from '@cbhq/cds-web/typography';

import { ReactComponent as AleoImg } from ':cloud/assets/Protocols/Logos/aleo.svg';
import { ReactComponent as AptosImg } from ':cloud/assets/Protocols/Logos/aptos.svg';
import { ReactComponent as AvalancheImg } from ':cloud/assets/Protocols/Logos/avalanche.svg';
import { ReactComponent as AxelarImg } from ':cloud/assets/Protocols/Logos/axelar.svg';
import { ReactComponent as CardanoImg } from ':cloud/assets/Protocols/Logos/cardano.svg';
import { ReactComponent as CelestiaImg } from ':cloud/assets/Protocols/Logos/celestia.svg';
import { ReactComponent as CosmosImg } from ':cloud/assets/Protocols/Logos/cosmos.svg';
import { ReactComponent as DydxImg } from ':cloud/assets/Protocols/Logos/dydx.svg';
import { ReactComponent as EigenLayerImg } from ':cloud/assets/Protocols/Logos/eigenlayer.svg';
import { ReactComponent as EthereumImg } from ':cloud/assets/Protocols/Logos/ethereum.svg';
import { ReactComponent as FlowImg } from ':cloud/assets/Protocols/Logos/flow.svg';
import { ReactComponent as NearImg } from ':cloud/assets/Protocols/Logos/near.svg';
import { ReactComponent as OsmosisImg } from ':cloud/assets/Protocols/Logos/osmosis.svg';
import { ReactComponent as PolkadotImg } from ':cloud/assets/Protocols/Logos/polkadot.svg';
import { ReactComponent as PolygonImg } from ':cloud/assets/Protocols/Logos/polygon.svg';
import { ReactComponent as SolanaImg } from ':cloud/assets/Protocols/Logos/solana.svg';
import { ReactComponent as SuiImg } from ':cloud/assets/Protocols/Logos/sui.svg';
import { ReactComponent as ZetachainImg } from ':cloud/assets/Protocols/Logos/zetachain.svg';

interface NetworkData {
  network: string | ReactElement;
  networkName?: string;
  asset: string;
  Logo: FunctionComponent<
    SVGProps<SVGSVGElement> & {
      title?: string;
    }
  >;
  hasApi: boolean;
  hasCommunityValidators: boolean | ReactElement;
  hasDedicatedValidators: boolean;
  estimatedRewards: string;
  commission: string;
  delegationMinimum: string | ReactElement;
  payoutPeriod: string;
  canCompoundRewards: boolean;
  unbondingPeriod: string | ReactElement;
  communityValidatorAddress: string | ReactElement;
  guide: string;
}

const networkData: NetworkData[] = [
  {
    network: (
      <HStack alignItems="center" gap={1}>
        <EthereumImg height={24} width={24} />
        <TextHeadline as="p">Ethereum</TextHeadline>
        <Tooltip content="In addition to a dedicated validator, users can stake ETH without any minimums on shared Ethereum validators when accessed through our Staking API.">
          <Icon name="info" size="s" color="foregroundMuted" />
        </Tooltip>
      </HStack>
    ),
    networkName: 'Ethereum',
    asset: 'ETH',
    Logo: EthereumImg,
    hasApi: true,
    hasCommunityValidators: true,
    hasDedicatedValidators: true,
    estimatedRewards: '3.57%',
    commission: '-',
    delegationMinimum: (
      <HStack alignItems="center" gap={1}>
        <TextBody as="p" color="foregroundMuted">
          0.01
        </TextBody>
        <Tooltip
          content={
            <TextBody as="p">
              For{' '}
              <Link
                href="https://docs.cdp.coinbase.com/staking/docs/protocols/partial-eth/staking"
                openInNewWindow
              >
                Partial ETH Staking
              </Link>
            </TextBody>
          }
        >
          <Icon name="info" size="s" color="foregroundMuted" />
        </Tooltip>
      </HStack>
    ),
    payoutPeriod: '6.4 min',
    canCompoundRewards: true,
    unbondingPeriod: 'Variable',
    communityValidatorAddress: (
      <TextBody as="p" color="foregroundMuted">
        -
      </TextBody>
    ),
    guide: 'https://www.coinbase.com/developer-platform/discover/protocol-guides/guide-to-eth',
  },
  {
    network: 'Solana',
    asset: 'SOL',
    Logo: SolanaImg,
    hasApi: true,
    hasCommunityValidators: true,
    hasDedicatedValidators: true,
    estimatedRewards: '6.89%',
    commission: '8.00%',
    delegationMinimum: '0.01',
    payoutPeriod: '2-3 days',
    canCompoundRewards: true,
    unbondingPeriod: '2 days',
    communityValidatorAddress: 'beefKGBWeSpHzYBHZXwp5So7wdQGX6mu4ZHCsH3uTar',
    guide: 'https://www.coinbase.com/developer-platform/discover/protocol-guides/guide-to-solana',
  },
  {
    network: 'Aleo',
    asset: 'ALEO',
    Logo: AleoImg,
    hasApi: false,
    hasCommunityValidators: true,
    hasDedicatedValidators: false,
    estimatedRewards: '29.00%',
    commission: '10.00%',
    delegationMinimum: '10,000',
    payoutPeriod: '2 sec',
    canCompoundRewards: true,
    unbondingPeriod: '1 Hour',
    communityValidatorAddress: 'aleo1m5vc6da037erge36scdmefk0dcnrjk9tu04zyedfvxunwcwd3vxqtcy7ln',
    guide: 'https://www.coinbase.com/developer-platform/discover/protocol-guides/guide-to-aleo',
  },
  {
    network: 'Aptos',
    asset: 'APT',
    Logo: AptosImg,
    hasApi: false,
    hasCommunityValidators: true,
    hasDedicatedValidators: true,
    estimatedRewards: '7.00%',
    commission: '8.00%',
    delegationMinimum: '11',
    payoutPeriod: '2 hrs',
    canCompoundRewards: true,
    // unbondingPeriod: '2 hrs', // Tooltip: Funds are available on the next validator unlock date, occurring every 30 days
    unbondingPeriod: (
      <HStack alignItems="center" gap={1}>
        <TextBody as="p" color="foregroundMuted">
          2 hrs
        </TextBody>
        <Tooltip
          content={
            <TextBody as="p">
              Funds are available on the next validator unlock date, occurring every 30 days.
            </TextBody>
          }
        >
          <Icon name="info" size="s" color="foregroundMuted" />
        </Tooltip>
      </HStack>
    ),
    communityValidatorAddress: '0x9c721c79ee082aafcdd99b1a71a833accdc48dba1a9a1bc5b5f8cc47ff7d49c0',
    guide: 'https://www.coinbase.com/developer-platform/discover/protocol-guides/guide-to-aptos',
  },
  {
    network: 'Avalanche',
    asset: 'AVAX',
    Logo: AvalancheImg,
    hasApi: false,
    hasCommunityValidators: true,
    hasDedicatedValidators: true,
    estimatedRewards: '7.91%',
    commission: '2.00%',
    delegationMinimum: '25',
    payoutPeriod: '14-365 days',
    canCompoundRewards: false,
    unbondingPeriod: '-',
    communityValidatorAddress: 'NodeID-CiKdcSyNH27re2W17ygscpZ4xG7474E5U',
    guide:
      'https://www.coinbase.com/developer-platform/discover/protocol-guides/guide-to-avalanche',
  },
  {
    network: 'Axelar',
    asset: 'AXL',
    Logo: AxelarImg,
    hasApi: false,
    hasCommunityValidators: true,
    hasDedicatedValidators: true,
    estimatedRewards: '7.07%',
    commission: '10.00%',
    delegationMinimum: '0.01',
    payoutPeriod: '6 sec',
    canCompoundRewards: true,
    unbondingPeriod: '7 days',
    communityValidatorAddress: 'axelarvaloper1nqe0ggecgsyaegl4t6m6k4786cd29xjt4znsf5',
    guide: 'https://www.coinbase.com/developer-platform/discover/protocol-guides/guide-to-axelar',
  },
  {
    network: 'Cardano',
    asset: 'ADA',
    Logo: CardanoImg,
    hasApi: false,
    hasCommunityValidators: true,
    hasDedicatedValidators: true,
    estimatedRewards: '2.78%',
    commission: '5% + 340',
    delegationMinimum: '10',
    payoutPeriod: '5 days',
    canCompoundRewards: true,
    unbondingPeriod: '-',
    communityValidatorAddress: '6804118fe78be9bf9213b9e829803952be87814d28b305fa52bf11d9',
    guide: 'https://www.coinbase.com/developer-platform/discover/protocol-guides/guide-to-cardano',
  },
  {
    network: 'Celestia',
    asset: 'TIA',
    Logo: CelestiaImg,
    hasApi: false,
    hasCommunityValidators: true,
    hasDedicatedValidators: true,
    estimatedRewards: '9.92%',
    commission: '10.00%',
    delegationMinimum: '1',
    payoutPeriod: '12 sec',
    canCompoundRewards: false,
    unbondingPeriod: '21 days',
    communityValidatorAddress: 'celestiavaloper1geuw5f6u3n5l4ne2d2nze3fehxf023f7s8h08t',
    guide: 'https://www.coinbase.com/developer-platform/discover/protocol-guides/guide-to-celestia',
  },
  {
    network: 'Cosmos',
    asset: 'ATOM',
    Logo: CosmosImg,
    hasApi: false,
    hasCommunityValidators: true,
    hasDedicatedValidators: true,
    estimatedRewards: '18.01%',
    commission: '8.00%',
    delegationMinimum: '1',
    payoutPeriod: '7 sec',
    canCompoundRewards: true,
    unbondingPeriod: '21 days',
    communityValidatorAddress: 'cosmosvaloper1crqm3598z6qmyn2kkcl9dz7uqs4qdqnr6s8jdn',
    guide: 'https://www.coinbase.com/developer-platform/discover/protocol-guides/guide-to-cosmos',
  },
  {
    network: 'dYdX',
    asset: 'DYDX',
    Logo: DydxImg,
    hasApi: false,
    hasCommunityValidators: true,
    hasDedicatedValidators: true,
    estimatedRewards: '8.22%',
    commission: '10.00%',
    delegationMinimum: '0.01',
    payoutPeriod: '1 sec',
    canCompoundRewards: false,
    unbondingPeriod: '30 days',
    communityValidatorAddress: 'dydxvaloper1v67nld0mc6ll2y66cttls2qpuwnx5704yu43l9',
    guide: 'https://www.coinbase.com/developer-platform/discover/protocol-guides/guide-to-dydx',
  },
  {
    network: 'EigenLayer',
    asset: 'ETH',
    Logo: EigenLayerImg,
    hasApi: false,
    hasCommunityValidators: <Icon name="circleCheckmark" size="m" color="positive" />,
    hasDedicatedValidators: false,
    estimatedRewards: '-',
    commission: '10.00%',
    delegationMinimum: '0.01',
    payoutPeriod: '-',
    canCompoundRewards: true,
    unbondingPeriod: '7 days',
    communityValidatorAddress: (
      <Tooltip
        content={
          <TextBody as="p">
            <Link
              href="https://app.eigenlayer.xyz/operator/0x0750972588a6fa8655e6ca4bd14de030b8666a47"
              openInNewWindow
            >
              Coinbase Operators
            </Link>{' '}
            support EigenDA among other Actively Validated Services (AVSs).
          </TextBody>
        }
      >
        <Icon name="info" size="m" color="foregroundMuted" />
      </Tooltip>
    ),
    guide:
      'https://www.coinbase.com/developer-platform/discover/protocol-guides/guide-to-eigenlayer',
  },
  {
    network: 'Flow',
    asset: 'FLOW',
    Logo: FlowImg,
    hasApi: false,
    hasCommunityValidators: true,
    hasDedicatedValidators: true,
    estimatedRewards: '11.54%',
    commission: '8.00%',
    delegationMinimum: '0.01',
    payoutPeriod: '7 days',
    canCompoundRewards: false,
    unbondingPeriod: '7-14 days',
    communityValidatorAddress: '48ba5aabeaf1a7c1bc6a96b20c8e9f6cbe7f6b3c77faec3b77fa66a630abf8c7',
    guide: 'https://www.coinbase.com/developer-platform/discover/protocol-guides/guide-to-flow',
  },
  {
    network: 'NEAR',
    asset: 'NEAR',
    Logo: NearImg,
    hasApi: false,
    hasCommunityValidators: true,
    hasDedicatedValidators: true,
    estimatedRewards: '8.89%',
    commission: '10.00%',
    delegationMinimum: '0.1',
    payoutPeriod: '12 hrs',
    canCompoundRewards: true,
    unbondingPeriod: '36-48 hrs',
    communityValidatorAddress: 'bisontrails.poolv1.near',
    guide: '',
  },
  {
    network: 'Osmosis',
    asset: 'OSMO',
    Logo: OsmosisImg,
    hasApi: false,
    hasCommunityValidators: true,
    hasDedicatedValidators: true,
    estimatedRewards: '14.59%',
    commission: '10.00%',
    delegationMinimum: '0.1',
    payoutPeriod: '24 hrs',
    canCompoundRewards: false,
    unbondingPeriod: '14 days',
    communityValidatorAddress: 'osmovaloper1q4mlwtw3ncve72na65xacck557wcknc8j0lmke',
    guide: 'https://www.coinbase.com/developer-platform/discover/protocol-guides/guide-to-osmosis',
  },
  {
    network: 'Polkadot',
    asset: 'DOT',
    Logo: PolkadotImg,
    hasApi: false,
    hasCommunityValidators: true,
    hasDedicatedValidators: true,
    estimatedRewards: '11.40%',
    commission: '8.00%',
    delegationMinimum: '2',
    payoutPeriod: '24 hrs',
    canCompoundRewards: true,
    unbondingPeriod: '28 days',
    communityValidatorAddress: '1VrKDfXunzstY5uxPpjArUbZekirGXcpMDYvCBJmjV1KdEm',
    guide: 'https://www.coinbase.com/developer-platform/discover/protocol-guides/guide-to-polkadot',
  },
  {
    network: 'Polygon',
    asset: 'MATIC',
    Logo: PolygonImg,
    hasApi: false,
    hasCommunityValidators: true,
    hasDedicatedValidators: true,
    estimatedRewards: '5.81%',
    commission: '8.00%',
    delegationMinimum: '1',
    payoutPeriod: '34 min',
    canCompoundRewards: false,
    unbondingPeriod: '3-4 days',
    communityValidatorAddress: '0x1092859ddf5f2a691bdfe29b666390eacfea0818',
    guide: 'https://www.coinbase.com/developer-platform/discover/protocol-guides/guide-to-polygon',
  },
  {
    network: 'Sui',
    asset: 'SUI',
    Logo: SuiImg,
    hasApi: false,
    hasCommunityValidators: true,
    hasDedicatedValidators: true,
    estimatedRewards: '3.09%',
    commission: '10.00%',
    delegationMinimum: '1',
    payoutPeriod: '24 hrs',
    canCompoundRewards: true,
    unbondingPeriod: '1 day',
    communityValidatorAddress: '0x0350925241ae8d4083fe4b1104e14dd28d2ffeced9d7575551c50c41b99995fb',
    guide: 'https://www.coinbase.com/developer-platform/discover/protocol-guides/guide-to-sui',
  },
  {
    network: 'Zetachain',
    asset: 'ZETA',
    Logo: ZetachainImg,
    hasApi: false,
    hasCommunityValidators: true,
    hasDedicatedValidators: true,
    estimatedRewards: '6.41%',
    commission: '10.00%',
    delegationMinimum: '1',
    payoutPeriod: '6 sec',
    canCompoundRewards: true,
    unbondingPeriod: '21 days',
    communityValidatorAddress: 'zetavaloper1plemdt5yuvqz70kqnyyha2tfs94jc40cqls89x',
    guide:
      'https://www.coinbase.com/developer-platform/discover/protocol-guides/guide-to-zetachain',
  },
];

export default networkData;
